import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactLoading from 'react-loading';
import { Box, Typography, Backdrop, TextField, Button } from '@mui/material';

import Modal from '../../../ui/Modal';
import { fetchDisputeMessages, sendDisputeMessage } from '../../../modules/disputes/thunks';
import { CLEAR_DISPUTE_MESSAGE_DATA } from '../../../modules/disputes/reducer';
import { DISPUTES_ACTIONS } from './utils';

const ViewMessagesModal = ({ viewMessageDispute = {}, onClose }) => {
  const messageRef = useRef('');
  const dispatch = useDispatch();

  const [isOpen, setOpen] = useState(false);
  const [sendMessageLoading, setSendMessageLoading] = useState(false);
  const [error, setError] = useState('');

  const { houseRef } = useSelector(store => store.house) || {};
  const { userRef } = useSelector(store => store.user) || {};
  const messages = useSelector(store => store.disputes.messages) || null;
  const fetchMessagesLoader = useSelector(store => store.disputes.fetchMessagesLoader) || false;
  const sendDisputeMessageData = useSelector(store => store.disputes.sendDisputeMessageData) || {};

  const { disputeRef, messageHeading, openDispute, disputeID } = viewMessageDispute;
  const { success } = sendDisputeMessageData || {};
  const isMessageSend = success === 'true';

  const closeModal = () => {
    setOpen(false);
    onClose();
  };

  const sendDisputeMessageHandler = () => {
    const message = messageRef.current.value;
    if (message) {
      setSendMessageLoading(true);
      dispatch(
        sendDisputeMessage(
          houseRef,
          disputeRef,
          {
            action: DISPUTES_ACTIONS.SEND,
            message: messageRef.current.value,
            disputeIDs: [disputeID],
            houseUserRef: userRef
          },
          () => {
            setSendMessageLoading(false);
            setError('');
          }
        )
      );
    } else {
      setError('Message is required.');
    }
  };

  useEffect(() => {
    if (disputeRef) dispatch(fetchDisputeMessages(houseRef, disputeRef, true));

    return () => {
      if (!isOpen) dispatch({ type: CLEAR_DISPUTE_MESSAGE_DATA });
    };
  }, [disputeRef]);

  useEffect(() => {
    if (messages) setOpen(true);
  }, [messages]);

  if (fetchMessagesLoader) {
    return (
      <Backdrop sx={{ zIndex: 111 }} open>
        <ReactLoading type="spin" color="black" />
      </Backdrop>
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={closeModal}>
      <Modal.Header toggle={closeModal}>Messages</Modal.Header>
      <Modal.Body>
        <Box mb={1}>
          <Typography variant="body2" fontWeight="bold">
            {messageHeading}
          </Typography>
        </Box>
        {messages?.length > 0 ? (
          <Box className="message-box">
            {messages.map(({ sender, sendDate, message }) => (
              <Box key={sendDate + message} display="flex">
                <Typography variant="body2">
                  <span className="message-heading">{`${sender} ${sendDate}: `}</span>
                  <span className="message-text">{message}</span>
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="red" mt={2}>
            No messages to display
          </Typography>
        )}
        {openDispute && !isMessageSend && (
          <Box mt={1}>
            <TextField
              placeholder="Please write your message here."
              inputRef={messageRef}
              className="buyer-message-text"
              id="outlined-textarea"
              multiline
              rows={5}
              sx={{ maxWidth: '440px' }}
              variant="standard"
            />
            {error && (
              <Typography variant="body2" color="red" mt={1}>
                {error}
              </Typography>
            )}
          </Box>
        )}
      </Modal.Body>
      <Modal.Footer>
        {openDispute && (
          <Box>
            {isMessageSend && (
              <Box mb={0.5}>
                <Typography variant="body2">Message Sent!</Typography>
              </Box>
            )}
            <Box>
              <Button
                className={`cancel-action ${isMessageSend ? 'hidden' : ''}`}
                onClick={closeModal}
              >
                Cancel
              </Button>
              <Button
                className="submit-action"
                variant="contained"
                onClick={isMessageSend ? closeModal : sendDisputeMessageHandler}
                disabled={sendMessageLoading}
              >
                {isMessageSend ? 'Close Window' : 'Send Message'}
                {sendMessageLoading && (
                  <ReactLoading className="ml-1" type="spin" height="auto" width={15} />
                )}
              </Button>
            </Box>
          </Box>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ViewMessagesModal;
